import type { ReactNode } from 'react';
import { useCallback, useDeferredValue, useState } from 'react';
import IntersectionObserver from '@researchgate/react-intersection-observer';
import { Box, Bones, useTheme } from '@simplywallst/ui-core';
import useResizeObserver from 'use-resize-observer';
import { ViewMode } from '@/pages/CompanyReport';
import { useSelector } from 'react-redux';
import { getViewMode } from '@/redux/location/selectors';
import useWindowSize from '@/hooks/useWindowSize';
import type { ChangeHandler } from '@researchgate/react-intersection-observer/typings/types';
import { useIntersectionRendererContext } from './IntersectionRendererProvider';
import { useCompareCompanyId } from '../CompareCompanies/CompanyIDProvider';
import { useVisibleSectionsIsPristine } from '../VisibleSectionsContextProvider';

/**
 * Executes the intersection observer onChange every 10% change when not visible
 */
export const INTERSECTION_THRESHOLDS_WHEN_INACTIVE = new Array(11)
  .fill(0)
  .map((_, i) => (i * 10) / 100);

const MOBILE_FOOTER_HEIGHT = 56;

type Props = {
  children: ReactNode;
  initialMinHeight?: number | string;
};

/**
 * On client, it will only render the component if it's in the viewport.
 * As soon as it's rendered for the first time, it will measure the height
 * so that upon revisiting the component there is no CLS.
 *
 * By default it renders on the server to allow for SEO indexing.
 */
const IntersectionRenderer = ({ children, initialMinHeight = 300 }: Props) => {
  const { intersectionDisabled } = useIntersectionRendererContext();
  const isPristine = useVisibleSectionsIsPristine();
  const companyId = useCompareCompanyId();
  const viewMode = useSelector(getViewMode);
  const isPrintMode = viewMode === ViewMode.Print;
  const [inView, setInView] = useState(false);
  const [lastHeight, setLastHeight] = useState<number | null>(null);
  // Disables intersection observer in print (PDF) mode, server or in company compare
  const forceInView = Boolean(
    isPrintMode ||
      RUNTIME_ENV === 'server' ||
      RUNTIME_ENV === 'native' ||
      companyId ||
      isPristine
  );
  const targetInView = forceInView || inView;
  const inViewDeferred = useDeferredValue(targetInView);
  const windowSize = useWindowSize();

  const { ref } = useResizeObserver({
    onResize: ({ height }) => {
      targetInView && height && height !== lastHeight && setLastHeight(height);
    },
  });

  const theme = useTheme();

  const isDesktopLayout = Boolean(
    windowSize.width && windowSize.width >= theme.lgMinRaw
  );

  const onIntersection: ChangeHandler = useCallback(
    ({ intersectionRect }) => {
      const { top, height: intersectingHeight } = intersectionRect;
      const elementIsAtBottom = top > 0;
      const bottomOffset = isDesktopLayout ? 0 : MOBILE_FOOTER_HEIGHT;
      const topOffset = isDesktopLayout
        ? theme.desktopTopbarHeight
        : theme.mobileTopbarHeight;

      if (elementIsAtBottom) {
        return setInView(intersectingHeight > bottomOffset);
      } else {
        return setInView(intersectingHeight > topOffset);
      }
    },
    [
      setInView,
      isDesktopLayout,
      theme.desktopTopbarHeight,
      theme.mobileTopbarHeight,
    ]
  );

  const minHeight =
    lastHeight && lastHeight > 0 ? lastHeight : initialMinHeight;
  const minHeightRule =
    typeof minHeight === 'string' ? minHeight : `${minHeight}px`;

  return (
    <IntersectionObserver
      threshold={
        !inViewDeferred
          ? (INTERSECTION_THRESHOLDS_WHEN_INACTIVE as unknown as number[])
          : 0
      }
      rootMargin={
        inViewDeferred && windowSize.height
          ? `${windowSize.height * 1.5}px` // Keep the content mounted for 1.5vh after it left viewport
          : '0px'
      }
      onChange={onIntersection}
      disabled={forceInView || intersectionDisabled}
    >
      <Box role="feed" aria-hidden={!inViewDeferred} borderRadius="x10space">
        {inViewDeferred ? (
          <Box ref={ref}>{children}</Box>
        ) : (
          <Bones
            minHeight={minHeightRule}
            borderRadius="x1spacePx"
            backgroundColor="black"
          />
        )}
      </Box>
    </IntersectionObserver>
  );
};

export default IntersectionRenderer;
